import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function ChecklistDetail({ assignment, onBack }) {
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [notes, setNotes] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const data = await api.get(`/assignments/${assignment.id}/progress`);
      setProgressData(data);
    } catch (err) {
      console.error('Failed to load progress:', err);
      setError('Failed to load checklist details');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteItem = (itemData) => {
    const { item, progress, isBlocked } = itemData;
    
    if (isBlocked) {
      alert('This item is blocked by dependencies. Complete the required items first.');
      return;
    }

    if (progress?.status === 'COMPLETED') {
      return; // Already completed
    }

    if (item.requiresEvidence) {
      setSelectedItem(itemData);
      setNotes(progress?.notes || '');
      setShowEvidenceModal(true);
    } else {
      // Complete without evidence
      completeItemWithoutEvidence(progress.id);
    }
  };

  const handleSkipItem = (itemData) => {
    const { item, progress } = itemData;
    
    if (!item.isRemovableByUser) {
      alert('This item cannot be skipped.');
      return;
    }

    setSelectedItem(itemData);
    setSkipReason(progress?.skippedReason || '');
    setShowSkipModal(true);
  };

  const completeItemWithoutEvidence = async (progressId) => {
    try {
      await api.put(`/progress/${progressId}`, {
        status: 'COMPLETED',
        notes: null,
        evidenceUrls: [],
        skippedReason: null
      });
      await loadProgress();
    } catch (err) {
      alert('Failed to complete item: ' + err.message);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleSubmitEvidence = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');

    try {
      const evidenceUrls = [];

      // Upload each file
      for (const file of selectedFiles) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
          reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
        });
        reader.readAsDataURL(file);
        const base64Data = await base64Promise;

        // Upload to backend
        const uploadResponse = await api.post('/files', {
          mimeType: file.type,
          dataBase64: base64Data
        });

        evidenceUrls.push(uploadResponse.url);
      }

      // Update progress with evidence
      await api.put(`/progress/${selectedItem.progress.id}`, {
        status: 'COMPLETED',
        notes: notes,
        evidenceUrls: evidenceUrls,
        skippedReason: null
      });

      await loadProgress();
      handleCloseEvidenceModal();
    } catch (err) {
      setError('Failed to submit evidence: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitSkip = async (e) => {
    e.preventDefault();
    setError('');

    if (!skipReason.trim()) {
      setError('Please provide a reason for skipping');
      return;
    }

    try {
      await api.put(`/progress/${selectedItem.progress.id}`, {
        status: 'SKIPPED',
        notes: null,
        evidenceUrls: [],
        skippedReason: skipReason
      });

      await loadProgress();
      handleCloseSkipModal();
    } catch (err) {
      setError('Failed to skip item: ' + err.message);
    }
  };

  const handleCloseEvidenceModal = () => {
    setShowEvidenceModal(false);
    setSelectedItem(null);
    setNotes('');
    setSelectedFiles([]);
    setError('');
  };

  const handleCloseSkipModal = () => {
    setShowSkipModal(false);
    setSelectedItem(null);
    setSkipReason('');
    setError('');
  };

  const getItemStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'var(--success-600)';
      case 'SKIPPED': return 'var(--warning-600)';
      case 'PENDING': return 'var(--gray-400)';
      default: return 'var(--gray-400)';
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  if (!progressData) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <h3 className="empty-state-title">Failed to Load</h3>
          <p className="empty-state-description">{error || 'Unable to load checklist details'}</p>
          <button className="btn btn-primary" onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  const { template, items, stats } = progressData;
  const completionPercentage = stats.total > 0 ? Math.round(((stats.completed + stats.skipped) / stats.total) * 100) : 0;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="btn btn-secondary" onClick={onBack} data-testid="back-to-checklists">
            &larr; Back to Checklists
          </button>
          <div style={{ flex: 1 }}>
            <h1 className="page-title">{template?.name}</h1>
            <p className="page-subtitle">{template?.description}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Progress</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
              {stats.completed + stats.skipped} of {stats.total} items completed
            </p>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-blue-600)' }}>
            {completionPercentage}%
          </div>
        </div>
        <div style={{ padding: '0 1.5rem 1.5rem' }}>
          <div style={{ 
            width: '100%', 
            height: '8px', 
            backgroundColor: 'var(--gray-200)', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${completionPercentage}%`,
              height: '100%',
              backgroundColor: 'var(--success-600)',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', fontSize: '0.875rem' }}>
            <div>
              <span style={{ color: 'var(--success-600)', fontWeight: '600' }}>✓ {stats.completed}</span> Completed
            </div>
            <div>
              <span style={{ color: 'var(--warning-600)', fontWeight: '600' }}>⊘ {stats.skipped}</span> Skipped
            </div>
            <div>
              <span style={{ color: 'var(--gray-600)', fontWeight: '600' }}>○ {stats.pending}</span> Pending
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Checklist Items</h2>
        </div>
        <div style={{ padding: '1.5rem' }}>
          {items.map((itemData, index) => {
            const { item, progress, isBlocked } = itemData;
            const isCompleted = progress?.status === 'COMPLETED';
            const isSkipped = progress?.status === 'SKIPPED';
            
            return (
              <div
                key={item.id}
                style={{
                  padding: '1.5rem',
                  marginBottom: '1rem',
                  backgroundColor: isCompleted ? 'var(--success-100)' : isSkipped ? 'var(--warning-100)' : 'var(--white)',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '8px',
                  opacity: isBlocked ? 0.6 : 1,
                }}
                data-testid={`item-${item.id}`}
              >
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'start' }}>
                  <div style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    borderRadius: '50%',
                    backgroundColor: getItemStatusColor(progress?.status),
                    color: 'var(--white)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    flexShrink: 0,
                  }}>
                    {isCompleted ? '✓' : isSkipped ? '⊘' : index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: '600' }}>
                        {item.title}
                        {item.requiresEvidence && <span style={{ marginLeft: '0.5rem' }}>📸</span>}
                        {item.externalUrl && (
                          <a 
                            href={item.externalUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ marginLeft: '0.5rem', fontSize: '1rem' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            🔗
                          </a>
                        )}
                        {isBlocked && <span className="badge badge-danger" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>Blocked</span>}
                      </h3>
                      {!isCompleted && !isSkipped && !isBlocked && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            className="btn btn-primary btn-small"
                            onClick={() => handleCompleteItem(itemData)}
                            data-testid={`complete-item-${item.id}`}
                          >
                            ✓ Complete
                          </button>
                          {item.isRemovableByUser && (
                            <button
                              className="btn btn-secondary btn-small"
                              onClick={() => handleSkipItem(itemData)}
                              data-testid={`skip-item-${item.id}`}
                            >
                              Skip
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {item.description && (
                      <div 
                        style={{ fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: '0.5rem' }}
                        dangerouslySetInnerHTML={{ __html: item.description }}
                      />
                    )}
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                      {item.dueOffsetDays !== null && <span>Due: +{item.dueOffsetDays} days</span>}
                      {isBlocked && <span style={{ color: 'var(--danger-600)' }}>⚠️ Complete dependencies first</span>}
                    </div>
                    {progress?.notes && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: 'var(--gray-100)', borderRadius: '6px' }}>
                        <strong style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>Notes:</strong>
                        <p style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>{progress.notes}</p>
                      </div>
                    )}
                    {progress?.skippedReason && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: 'var(--warning-100)', borderRadius: '6px' }}>
                        <strong style={{ fontSize: '0.75rem', color: 'var(--warning-700)' }}>Skipped - Reason:</strong>
                        <p style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>{progress.skippedReason}</p>
                      </div>
                    )}
                    {progress?.evidenceUrls && progress.evidenceUrls.length > 0 && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <strong style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>Evidence Attached:</strong>
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                          {progress.evidenceUrls.map((url, i) => (
                            <span key={i} className="badge badge-success">📎 File {i + 1}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Evidence Modal */}
      {showEvidenceModal && selectedItem && (
        <div className="modal-overlay" onClick={handleCloseEvidenceModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Submit Evidence</h2>
            </div>
            <form onSubmit={handleSubmitEvidence}>
              <div className="modal-body">
                {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}
                
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  {selectedItem.item.title}
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '1.5rem' }}>
                  This item requires evidence of completion (photo or document)
                </p>

                <div className="form-group">
                  <label className="form-label" htmlFor="evidence-files">Upload Evidence *</label>
                  <input
                    id="evidence-files"
                    type="file"
                    className="form-input"
                    accept="image/*,.pdf,.doc,.docx"
                    multiple
                    onChange={handleFileSelect}
                    required
                    data-testid="evidence-file-input"
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
                    Upload photos, PDFs, or documents (multiple files allowed)
                  </p>
                  {selectedFiles.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <strong style={{ fontSize: '0.75rem' }}>Selected:</strong>
                      {selectedFiles.map((file, i) => (
                        <div key={i} style={{ fontSize: '0.75rem', color: 'var(--gray-700)' }}>
                          • {file.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="evidence-notes">Notes (Optional)</label>
                  <textarea
                    id="evidence-notes"
                    className="form-input"
                    placeholder="Add any additional notes about this completion..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="3"
                    data-testid="evidence-notes-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCloseEvidenceModal}
                  disabled={uploading}
                  data-testid="cancel-evidence"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={uploading || selectedFiles.length === 0}
                  data-testid="submit-evidence"
                >
                  {uploading ? 'Uploading...' : 'Submit & Complete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Skip Modal */}
      {showSkipModal && selectedItem && (
        <div className="modal-overlay" onClick={handleCloseSkipModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Skip Item</h2>
            </div>
            <form onSubmit={handleSubmitSkip}>
              <div className="modal-body">
                {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}
                
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  {selectedItem.item.title}
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '1.5rem' }}>
                  Please provide a reason for skipping this item
                </p>

                <div className="form-group">
                  <label className="form-label" htmlFor="skip-reason">Reason for Skipping *</label>
                  <textarea
                    id="skip-reason"
                    className="form-input"
                    placeholder="Explain why you are skipping this item..."
                    value={skipReason}
                    onChange={(e) => setSkipReason(e.target.value)}
                    rows="4"
                    required
                    data-testid="skip-reason-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCloseSkipModal}
                  data-testid="cancel-skip"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-danger"
                  data-testid="confirm-skip"
                >
                  Skip Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChecklistDetail;
